import { useQuery } from '@tanstack/react-query';
import { endOfDay, endOfHour, sub } from 'date-fns';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  MinusIcon,
  UsersIcon,
} from '@phosphor-icons/react';
import { Badge } from '@logspot-platform/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@logspot-platform/ui/tooltip';
import { AreaChart } from './charts/area-chart';
import { Widget } from './widget';
import { useWidgetsContext } from './widgets-context';
import { getDau } from './api/widgets-service';
import { numberWithCommas } from './lib/number-with-commas';
import type { DateRange } from './types';

function getVariant(diff: number) {
  if (diff < 0) {
    return 'error';
  } else if (diff > 0) {
    return 'success';
  } else {
    return 'neutral';
  }
}

export const DauWidget = ({ dateRange }: { dateRange: DateRange }) => {
  const { api, projectId, filterGroups } = useWidgetsContext();
  const prevDateRange = {
    interval: dateRange.interval,
    startDate: sub(dateRange.startDate, dateRange.period || { days: 1 }),
    endDate: dateRange.interval.includes('days')
      ? endOfDay(dateRange.startDate)
      : endOfHour(dateRange.startDate),
  };

  const { isLoading, data } = useQuery({
    queryKey: [`dau`, projectId, dateRange, filterGroups],
    queryFn: () =>
      getDau(api, {
        projectId,
        interval: dateRange.interval,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        filterGroups,
      }),
  });
  const { data: prevData } = useQuery({
    queryKey: [`prev-dau`, projectId, dateRange, filterGroups],
    queryFn: () =>
      getDau(api, {
        projectId,
        interval: prevDateRange.interval,
        startDate: prevDateRange.startDate,
        endDate: prevDateRange.endDate,
        filterGroups,
      }),
  });

  const currentTotal = parseInt(data?.total ?? '0') || 0;
  const currentLive = parseInt(data?.live ?? '0') || 0;
  const prevTotal = parseInt(prevData?.total ?? '0') || 0;

  const diff =
    prevTotal === 0 ? 100 : ((currentTotal - prevTotal) * 100) / prevTotal;
  const formattedDiff = Math.abs(diff).toFixed(0);
  return (
    <Widget
      loading={isLoading}
      Header={() => (
        <>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-xl font-medium">
              <UsersIcon className="text-primary" />
              <span className="font-semibold">Unique Users</span>
            </span>
            <span className="flex items-center gap-2">
              {!!data && (
                <div className="flex items-center gap-3">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-3xl font-black leading-none">
                          {numberWithCommas(currentTotal)}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>Unique Users</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <Badge variant={getVariant(diff)}>
                    {diff < 0 && <ArrowDownIcon weight="bold" />}
                    {diff === 0 && <MinusIcon weight="bold" />}
                    {diff > 0 && <ArrowUpIcon weight="bold" />}
                    <span className="text-muted-foreground">
                      {formattedDiff}%
                    </span>
                  </Badge>
                </div>
              )}
            </span>
          </div>
        </>
      )}
      SubHeader={() => (
        <div className="ml-5 flex items-center gap-1">
          <div className="space-x-2">
            <div className="inline-block size-3 animate-pulse rounded-full bg-green-500"></div>
            <span className="text-xl font-bold">{currentLive} Online</span>
          </div>
        </div>
      )}
    >
      {!isLoading && (
        <AreaChart
          data={data?.perDay ?? []}
          dateRange={dateRange}
          prevData={prevData?.perDay}
          prevDateRange={prevDateRange}
        />
      )}
    </Widget>
  );
};
